/**
 * Starting layouts for the two Intelligence dashboards.
 *
 * These are opinionated "what a company actually looks at every Monday"
 * boards. Widgets whose sources the viewer can't read are dropped at build
 * time (a manager never sees an empty "Cash on hand" card), and sections
 * left with nothing are dropped too.
 */

import { BRAND } from '@/lib/brand';
import { widgetSourceIds } from './catalog';
import { DEFAULT_SIZE, newId } from './layout';
import {
  DASHBOARD_LAYOUT_VERSION,
  type ChartType,
  type ChartWidget,
  type DashboardLayout,
  type DashboardSection,
  type DashboardWidget,
  type ForecastWidget,
  type MetricWidget,
  type TableWidget,
  type TextWidget,
} from './types';

type Size = { w?: number; h?: number };

function metric(id: string, opts: Size & { title?: string; sparkline?: boolean } = {}): MetricWidget {
  return {
    kind: 'metric',
    id: newId(),
    w: opts.w ?? DEFAULT_SIZE.metric.w,
    h: opts.h ?? DEFAULT_SIZE.metric.h,
    title: opts.title,
    metric: id,
    showSparkline: opts.sparkline ?? true,
    showDelta: true,
    target: null,
  };
}

function chart(
  chartType: ChartType,
  metrics: Array<string | { metric: string; color?: string; kind?: 'bar' | 'line' | 'area'; axis?: 'left' | 'right' }>,
  opts: Size & { title?: string; compare?: boolean; cumulative?: boolean; grain?: ChartWidget['grain'] } = {},
): ChartWidget {
  return {
    kind: 'chart',
    id: newId(),
    w: opts.w ?? DEFAULT_SIZE.chart.w,
    h: opts.h ?? DEFAULT_SIZE.chart.h,
    title: opts.title,
    chartType,
    series: metrics.map((m) => (typeof m === 'string' ? { metric: m } : m)),
    grain: opts.grain ?? 'auto',
    compare: opts.compare ?? false,
    cumulative: opts.cumulative ?? false,
    showLegend: true,
  };
}

function breakdown(chartType: 'donut' | 'hbar', id: string, opts: Size & { title?: string; limit?: number } = {}): ChartWidget {
  return {
    kind: 'chart',
    id: newId(),
    w: opts.w ?? 4,
    h: opts.h ?? DEFAULT_SIZE.chart.h,
    title: opts.title,
    chartType,
    series: [],
    breakdown: id,
    grain: 'auto',
    compare: false,
    cumulative: false,
    showLegend: true,
    limit: opts.limit,
  };
}

function table(id: string, opts: Size & { title?: string; limit?: number } = {}): TableWidget {
  return {
    kind: 'table',
    id: newId(),
    w: opts.w ?? DEFAULT_SIZE.table.w,
    h: opts.h ?? DEFAULT_SIZE.table.h,
    title: opts.title,
    table: id,
    columns: [],
    limit: opts.limit ?? 0,
    sort: null,
  };
}

function forecast(id: string, opts: Size & { title?: string; method?: ForecastWidget['method'] } = {}): ForecastWidget {
  return {
    kind: 'forecast',
    id: newId(),
    w: opts.w ?? DEFAULT_SIZE.forecast.w,
    h: opts.h ?? DEFAULT_SIZE.forecast.h,
    title: opts.title,
    source: id,
    method: opts.method ?? 'auto',
    horizon: 0,
    history: 0,
    showBand: true,
    showManual: true,
    itemId: null,
  };
}

function note(body: string, opts: Size & { title?: string } = {}): TextWidget {
  return {
    kind: 'text',
    id: newId(),
    w: opts.w ?? DEFAULT_SIZE.text.w,
    h: opts.h ?? DEFAULT_SIZE.text.h,
    title: opts.title,
    body,
  };
}

function section(title: string, widgets: DashboardWidget[], description?: string): DashboardSection {
  return { id: newId('s'), title, description, widgets };
}

function prune(sections: DashboardSection[], permitted: (sourceId: string) => boolean): DashboardLayout {
  const kept = sections
    .map((s) => ({
      ...s,
      widgets: s.widgets.filter((w) => widgetSourceIds(w).every(permitted)),
    }))
    // A section that only has notes left isn't worth showing.
    .filter((s) => s.widgets.some((w) => w.kind !== 'text'));
  return { version: DASHBOARD_LAYOUT_VERSION, sections: kept };
}

// ---------------------------------------------------------------------------
// KPIs
// ---------------------------------------------------------------------------

export function buildDefaultKpisLayout(permitted: (sourceId: string) => boolean): DashboardLayout {
  return prune(
    [
      section(
        'Financial health',
        [
          metric('fin.cash_on_hand'),
          metric('fin.net_cash_flow'),
          metric('fin.runway', { sparkline: false }),
          metric('fin.net_margin'),
          chart('combo', [{ metric: 'fin.cash_in', kind: 'bar' }, { metric: 'fin.cash_out', kind: 'bar', color: BRAND.freshSalmonDark }, { metric: 'fin.net_cash_flow', kind: 'line', color: BRAND.atmosphereDark }], {
            title: 'Cash in, cash out, net',
            w: 8,
          }),
          breakdown('donut', 'fin.ar_aging_buckets', { title: 'Receivables aging' }),
          chart('bar', ['fin.revenue', { metric: 'fin.expenses', color: BRAND.freshSalmonDark }], { title: 'Revenue vs expenses by month', w: 6 }),
          table('fin.ar_aging_table', { w: 6, limit: 6 }),
        ],
        'Cash position, burn and profitability from the bank feed and P&L.',
      ),
      section(
        'Sales',
        [
          metric('sales.revenue'),
          metric('sales.orders'),
          metric('sales.aov'),
          metric('sales.return_rate'),
          chart('area', ['sales.revenue'], { title: 'Order revenue', w: 8, compare: true }),
          breakdown('donut', 'sales.revenue_by_channel'),
          table('sales.top_products', { w: 6, limit: 6 }),
          chart('stacked', ['sales.revenue_shopify', 'sales.revenue_amazon', 'sales.revenue_b2b_portal', 'sales.revenue_manual'], {
            title: 'Revenue by channel over time',
            w: 6,
          }),
        ],
        'Demand, basket size and quality of revenue across channels.',
      ),
      section(
        'Operations',
        [
          metric('ops.units_produced'),
          metric('ops.first_pass_yield'),
          metric('ops.cycle_time'),
          metric('ops.open_defects', { sparkline: false }),
          chart('combo', [{ metric: 'ops.units_produced', kind: 'bar' }, { metric: 'ops.units_scrapped', kind: 'bar', color: BRAND.freshSalmonDark }, { metric: 'ops.first_pass_yield', kind: 'line', axis: 'right', color: BRAND.atmosphereDark }], {
            title: 'Output, scrap and yield',
            w: 8,
          }),
          breakdown('hbar', 'ops.units_by_station'),
          table('ops.open_defects_table', { w: 7, limit: 6 }),
          metric('ops.morale', { w: 5, h: 2 }),
        ],
        'Floor output and quality from Work Mode.',
      ),
      section(
        'Inventory & supply',
        [
          metric('inv.on_hand_value', { sparkline: false }),
          metric('inv.below_reorder', { sparkline: false }),
          metric('inv.stockouts', { sparkline: false }),
          metric('proc.open_po_value', { sparkline: false }),
          metric('proc.late_pos', { sparkline: false, w: 3 }),
          metric('proc.on_time_receipt', { sparkline: false, w: 3 }),
          metric('proc.lead_time', { sparkline: false, w: 3 }),
          metric('inv.days_of_cover', { sparkline: false, w: 3 }),
          table('inv.reorder_table', { w: 7, limit: 6 }),
          breakdown('hbar', 'proc.spend_by_vendor', { w: 5 }),
        ],
        'Stock health and the purchasing pipeline.',
      ),
      section(
        'Marketing',
        [
          metric('mkt.sessions'),
          metric('mkt.web_conversion_rate'),
          metric('mkt.ad_spend'),
          metric('mkt.roas'),
          chart('area', ['mkt.sessions', { metric: 'mkt.web_conversions', axis: 'right', color: BRAND.brightMossDark }], {
            title: 'Traffic and conversions',
            w: 8,
          }),
          breakdown('donut', 'mkt.sessions_by_channel'),
          table('mkt.campaigns_table', { w: 7, limit: 6 }),
          metric('mkt.followers', { w: 5 }),
        ],
        'Acquisition and paid-media efficiency.',
      ),
      section(
        'Projects',
        [
          metric('proj.open_tasks', { sparkline: false }),
          metric('proj.overdue_tasks', { sparkline: false }),
          metric('proj.on_time_completion', { sparkline: false }),
          metric('proj.tasks_completed'),
          breakdown('hbar', 'proj.overdue_by_project', { w: 5 }),
          table('proj.overdue_table', { w: 7, limit: 6 }),
        ],
        'Delivery health across projects.',
      ),
    ],
    permitted,
  );
}

// ---------------------------------------------------------------------------
// Forecast
// ---------------------------------------------------------------------------

export function buildDefaultForecastLayout(permitted: (sourceId: string) => boolean): DashboardLayout {
  return prune(
    [
      section(
        'Revenue outlook',
        [
          forecast('fc.revenue', { w: 8 }),
          note(
            'Each card fits a model to complete months of history and projects it forward. "Auto" backtests linear, Holt, seasonal and moving-average fits on the most recent months and keeps the most accurate. The shaded band is an ~80% range that widens with the horizon; dashed lines are plans you have entered.',
            { title: 'How to read these', w: 4, h: 4 },
          ),
          forecast('fc.net_income', { w: 6 }),
          forecast('fc.expenses', { w: 6 }),
        ],
        'Booked P&L projected forward against the plan.',
      ),
      section(
        'Cash outlook',
        [forecast('fc.cash_balance', { w: 12, h: 4 }), forecast('fc.cash_in', { w: 6 }), forecast('fc.cash_out', { w: 6 })],
        'Where the bank balance is heading at the current run rate.',
      ),
      section(
        'Demand outlook',
        [
          forecast('fc.sales_revenue', { w: 6 }),
          forecast('fc.sales_orders', { w: 6 }),
          forecast('fc.sales_units', { w: 6 }),
          forecast('fc.customers', { w: 6 }),
        ],
        'Order volume and value from the sales channels.',
      ),
      section(
        'Operations & marketing outlook',
        [forecast('fc.units_produced', { w: 4 }), forecast('fc.sessions', { w: 4 }), forecast('fc.ad_spend', { w: 4 })],
      ),
    ],
    permitted,
  );
}
